import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Copy, UserPlus, DollarSign, Trophy, Target, Gift, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const FRONTEND_URL = window.location.origin;
const BASE_PER_SIGNUP = 0.50;

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [customMsg, setCustomMsg] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [customizeOpen, setCustomizeOpen] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats").then(r => {
      setStats(r.data);
      setCustomMsg(r.data.user?.custom_message || "");
      setDisplayName(r.data.user?.display_name || "");
    }).catch(console.error);
  }, []);

  const joinLink = `${FRONTEND_URL}/${user?.affiliate_code}`;

  const copyLink = () => {
    try {
      navigator.clipboard.writeText(joinLink);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = joinLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const saveCustomize = async () => {
    try {
      await api.put("/ambassador/customize", { custom_message: customMsg, display_name: displayName });
      toast.success("Page customized!");
      setCustomizeOpen(false);
    } catch { toast.error("Failed to save"); }
  };

  const earnings = stats?.earnings;
  const totalSignups = earnings?.total_signups || 0;
  const userData = stats?.user || user;

  const statusStyles = {
    approved: "bg-green-500/10 text-green-400 border-green-500/20",
    pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    rejected: "bg-red-500/10 text-red-400 border-red-500/20",
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 animate-fade-in-up">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-white/90 tracking-tight">
            Welcome back, {user?.name}
          </h1>
          <p className="text-white/35 text-sm mt-1">
            Member UUID: <span className="font-mono text-xs text-white/20">{user?.member_uuid}</span>
          </p>
        </div>
        <Badge className={`${statusStyles[userData?.status] || statusStyles.pending} border text-xs font-medium`} data-testid="ambassador-status">
          {userData?.status || "pending"}
        </Badge>
      </div>

      {/* Referral Link */}
      <div className="glass rounded-2xl p-5 border-[#F7941D]/15 animate-fade-in-up" style={{ animationDelay: '60ms' }} data-testid="affiliate-link-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-[0.15em] text-white/30 font-medium mb-2">Your Referral Link</p>
            <p className="font-mono text-sm text-[#F7941D] truncate" data-testid="affiliate-link-text">{joinLink}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={copyLink} className="glass glass-hover rounded-full px-4 py-2 text-xs text-white/60 hover:text-white/90 flex items-center gap-2" data-testid="copy-affiliate-link-button">
              {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy"}
            </button>
            <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
              <DialogTrigger asChild>
                <button className="glass glass-hover rounded-full px-4 py-2 text-xs text-white/60 hover:text-white/90 flex items-center gap-2" data-testid="customize-page-button">
                  <Pencil className="w-4 h-4" />
                  Customize
                </button>
              </DialogTrigger>
              <DialogContent className="glass-strong rounded-2xl border-white/[0.08]">
                <DialogHeader><DialogTitle className="text-white/90">Customize Your Referral Page</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label className="text-white/60 text-sm font-medium">Display Name</Label>
                    <Input value={displayName} onChange={e => setDisplayName(e.target.value)} className="glass-input rounded-xl h-11" data-testid="customize-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/60 text-sm font-medium">Custom Message</Label>
                    <Input value={customMsg} onChange={e => setCustomMsg(e.target.value)} placeholder="A personal note shown on your referral page" className="glass-input rounded-xl h-11" data-testid="customize-message-input" />
                  </div>
                  <button className="w-full bg-[#F7941D] hover:bg-[#ff9f2e] text-black font-semibold rounded-full h-11 text-sm transition-all duration-300" onClick={saveCustomize} data-testid="save-customize-button">Save Changes</button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger" data-testid="metrics-grid">
        {[
          { icon: UserPlus, label: "Waitlist Signups", value: totalSignups, color: "text-blue-400", bg: "bg-blue-500/10" },
          { icon: DollarSign, label: "Base Earnings ($0.50/signup)", value: `$${(earnings?.base_earnings || 0).toFixed(2)}`, color: "text-green-400", bg: "bg-green-500/10" },
          { icon: Trophy, label: "Milestone Bonuses", value: `$${(earnings?.milestone_bonus || 0).toFixed(2)}`, color: "text-purple-400", bg: "bg-purple-500/10" },
          { icon: DollarSign, label: "Total Earnings", value: `$${(earnings?.total_earnings || 0).toFixed(2)}`, color: "text-[#F7941D]", bg: "bg-[#F7941D]/10" },
        ].map(({ icon: Icon, label, value, color, bg }, i) => (
          <div key={label} className="glass glass-hover rounded-2xl p-5 animate-fade-in-up">
            <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center mb-3`}>
              <Icon className={`w-[18px] h-[18px] ${color}`} strokeWidth={1.5} />
            </div>
            <p className={`font-mono text-2xl sm:text-3xl font-semibold ${color}`} data-testid={`metric-${i}`}>{value}</p>
            <p className="text-xs text-white/30 mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* Milestone Progress */}
      <div className="glass rounded-2xl p-5 animate-fade-in-up" style={{ animationDelay: '180ms' }} data-testid="milestone-progress">
        <h3 className="text-base font-semibold text-white/85 flex items-center gap-2 mb-4">
          <Target className="w-4 h-4 text-[#F7941D]" /> Milestone Progress
        </h3>
        {earnings && (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-white/45">{earnings.progress_to_next || 0} / {earnings.milestone_interval || 100} to next milestone</span>
              <span className="font-medium text-[#F7941D]">${earnings.per_milestone || 25} CAD bonus every {earnings.milestone_interval || 100} signups</span>
            </div>
            <Progress value={((earnings.progress_to_next || 0) / (earnings.milestone_interval || 100)) * 100} className="h-3 bg-white/[0.04]" />
            <div className="flex justify-between items-center mt-3">
              <span className="text-xs text-white/30">{earnings.milestones_hit || 0} milestones hit &middot; ${(earnings.milestone_bonus || 0).toFixed(2)} earned</span>
              <span className="text-xs text-[#F7941D] font-mono">Next at {earnings.next_milestone_at || 100} signups</span>
            </div>
          </div>
        )}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="glass rounded-xl p-3 text-center">
            <p className="text-xs text-white/30 mb-1">Per Signup</p>
            <p className="font-mono text-lg font-semibold text-green-400">${BASE_PER_SIGNUP}</p>
          </div>
          <div className="glass rounded-xl p-3 text-center">
            <p className="text-xs text-white/30 mb-1">Per 100 Milestone</p>
            <p className="font-mono text-lg font-semibold text-[#F7941D]">$25 CAD</p>
          </div>
          <div className="glass rounded-xl p-3 text-center">
            <p className="text-xs text-white/30 mb-1">Milestones Hit</p>
            <p className="font-mono text-lg font-semibold text-purple-400">{earnings?.milestones_hit || 0}</p>
          </div>
        </div>
      </div>

      {/* Chart */}
      {stats?.chart_data?.length > 0 && (
        <div className="glass rounded-2xl p-5 animate-fade-in-up" data-testid="signups-chart">
          <h3 className="text-base font-semibold text-white/85 mb-4">Signups (30 Days)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={stats.chart_data}>
              <defs>
                <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F7941D" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#F7941D" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} />
              <Tooltip contentStyle={{ backgroundColor: 'rgba(15,15,25,0.95)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', fontSize: '12px', color: '#f0f0f5', backdropFilter: 'blur(20px)' }} />
              <Area type="monotone" dataKey="signups" stroke="#F7941D" fill="url(#sg)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Beta Credit Info */}
      <div className="glass rounded-2xl p-5 border-[#F7941D]/10 animate-fade-in-up" data-testid="beta-credit-info">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-[#F7941D]/15 flex items-center justify-center shrink-0">
            <Gift className="w-5 h-5 text-[#F7941D]" />
          </div>
          <div>
            <p className="font-semibold text-white/80 text-sm">Every person who signs up gets a $50 credit</p>
            <p className="text-xs text-white/35 mt-0.5">When Eamico Studio launches in Beta on June 1st, 2026, each waitlist member receives $50 as a credit to their account.</p>
          </div>
        </div>
      </div>

      {/* Recent Signups */}
      <div className="glass rounded-2xl overflow-hidden animate-fade-in-up" data-testid="recent-signups">
        <div className="p-5 pb-3">
          <h3 className="text-base font-semibold text-white/85">Recent Waitlist Signups</h3>
        </div>
        <div className="px-5 pb-5">
          {(!stats?.recent_signups || stats.recent_signups.length === 0) ? (
            <p className="text-white/25 text-sm py-8 text-center">No signups yet. Share your referral link to start earning!</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left text-white/35 text-xs font-medium py-3 pr-4">Name</th>
                    <th className="text-left text-white/35 text-xs font-medium py-3 pr-4">Email</th>
                    <th className="text-right text-white/35 text-xs font-medium py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_signups.map((s, i) => (
                    <tr key={i} className="border-b border-white/[0.03] last:border-0">
                      <td className="text-sm text-white/70 py-3 pr-4">{s.first_name} {s.last_name}</td>
                      <td className="text-xs text-white/35 font-mono py-3 pr-4">{s.email}</td>
                      <td className="font-mono text-xs text-white/25 text-right py-3">{new Date(s.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
